/**
 * Persistent Video Component
 * Desktop video that shows only on signup step 1
 * Optimized for smooth playback on all devices
 * 
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import profileWave from '../../assets/images/profile-wave.png';
import cockpitLandingVideo from '../../assets/videos/cockpit-landing.webm';
import { useVideoStore } from '../../stores/videoStore';

export const PersistentVideo: React.FC = React.memo(() => {
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const showFromStore = useVideoStore((state) => state.showVideo);
  const shouldShow = showFromStore;
  const [isWebmSupported, setIsWebmSupported] = useState(true);
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  const isNativeApp = Capacitor.isNativePlatform();

  // Prefer public/ URLs so native + web can share the same assets (and allow different mobile/desktop videos).
  // We keep the imported asset as a last-resort fallback.
  const desktopWebmPublic = '/videos/auth-desktop.webm';
  const desktopMp4Public = '/videos/auth-desktop.mp4';
  const mobileWebmPublic = '/videos/auth-mobile.webm';
  const mobileMp4Public = '/videos/auth-mobile.mp4';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const v = document.createElement('video');
    const canPlay = v.canPlayType?.('video/webm; codecs="vp8, vorbis"') || v.canPlayType?.('video/webm') || '';
    setIsWebmSupported(Boolean(canPlay));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktopViewport(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // Mutually exclusive:
  // - Desktop video: desktop web only (not native)
  // - Mobile video: mobile web + native
  const showDesktopVideo = useMemo(
    () => Boolean(shouldShow && !isNativeApp && isDesktopViewport),
    [shouldShow, isNativeApp, isDesktopViewport],
  );
  const showMobileVideo = useMemo(
    () => Boolean(shouldShow && !showDesktopVideo),
    [shouldShow, showDesktopVideo],
  );

  // Initialize mobile video and keep it playing
  useEffect(() => {
    const video = mobileVideoRef.current;
    if (!video || !showMobileVideo) return;

    const playVideo = async () => {
      try {
        if (video.paused || video.ended) {
          video.currentTime = 0;
          await video.play();
        }
      } catch {
        // Silently handle autoplay restrictions
      }
    };

    playVideo();

    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => { });
    };
    const handlePause = () => {
      if (!video.ended) video.play().catch(() => { });
    };

    video.addEventListener('ended', handleEnded, { passive: true });
    video.addEventListener('pause', handlePause, { passive: true });

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
    };
  }, [showMobileVideo]);

  // Initialize desktop video and keep it playing
  useEffect(() => {
    const video = desktopVideoRef.current;
    if (!video || !showDesktopVideo) return;

    const playVideo = async () => {
      try {
        if (video.paused || video.ended) {
          video.currentTime = 0;
          await video.play();
        }
      } catch {
        // Silently handle autoplay restrictions
      }
    };

    playVideo();

    const handleEnded = () => {
      video.currentTime = 0;
      video.play().catch(() => { });
    };
    const handlePause = () => {
      if (!video.ended) video.play().catch(() => { });
    };

    video.addEventListener('ended', handleEnded, { passive: true });
    video.addEventListener('pause', handlePause, { passive: true });

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
    };
  }, [showDesktopVideo]);

  if (!shouldShow) return null;

  return (
    <>
      {/* Mobile video (top). Rounded corner only on bottom-right. */}
      {showMobileVideo && (
        <div className="w-full relative lg:hidden z-0 pointer-events-none">
          <div className="w-full overflow-hidden rounded-br-[5rem]">
            <div className='flex flex-row justify-center items-center'
              style={{
                width: '100%',
                height: 'auto',
                overflow: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                borderBottomRightRadius: '5rem',
                WebkitBorderBottomRightRadius: '5rem',
              }}
            >
              <video
                ref={mobileVideoRef}
                className="w-fit h-[28vh] block"
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  transform: 'translateZ(0)',
                  WebkitTransform: 'translateZ(0)',
                  pointerEvents: 'none',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  borderBottomRightRadius: '5rem',
                  WebkitBorderBottomRightRadius: '5rem',
                }}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={profileWave}
              >
                {/* iOS/WKWebView will prefer mp4. */}
                <source src={mobileMp4Public} type="video/mp4" />
                {isWebmSupported && <source src={mobileWebmPublic} type="video/webm" />}
                {/* Fallback to bundled webm so web doesn't go blank if public videos aren't present yet */}
                <source src={cockpitLandingVideo} type="video/webm" />
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Desktop video (left). All corners rounded. */}
      {showDesktopVideo && (
        <div
          className={`hidden lg:flex lg:items-center lg:justify-center lg:w-[45%] lg:fixed lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:z-20 lg:overflow-visible pointer-events-none transition-opacity duration-300 ${shouldShow ? 'opacity-100' : 'opacity-0'}`}
          style={{ visibility: shouldShow ? 'visible' : 'hidden', height: 'fit-content' }}
        >
          <div
            className="rounded-[0.75rem] overflow-hidden"
            style={{
              width: 'fit-content',
              maxWidth: '100%',
              paddingLeft: '2rem',
              paddingRight: '2rem',
              margin: '0 auto',
            }}
          >
            <div
              style={{
                width: 'fit-content',
                height: 'fit-content',
                borderRadius: '0.75rem',
                WebkitBorderRadius: '0.75rem',
                overflow: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                clipPath: 'inset(0 round 0.75rem)',
                WebkitClipPath: 'inset(0 round 0.75rem)',
              }}
            >
              <video
                ref={desktopVideoRef}
                className="block"
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center',
                  display: 'block',
                  transform: 'translateZ(0)',
                  WebkitTransform: 'translateZ(0)',
                  willChange: 'auto',
                  pointerEvents: 'none',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                }}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={profileWave}
              >
                <source src={desktopMp4Public} type="video/mp4" />
                {isWebmSupported && <source src={desktopWebmPublic} type="video/webm" />}
                <source src={cockpitLandingVideo} type="video/webm" />
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

PersistentVideo.displayName = 'PersistentVideo';
